const WORD_PATTERN = /[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu;
const VOWEL_GROUP_PATTERN = /[aeiouy]+/g;

export const SECTION_ORDER = ['Hook', 'Verse 1', 'Verse 2', 'Outro'];
export const MAX_LINES_PER_SECTION = 16;
export const MAX_SONG_LINES = 48;

const wordsIn = (text = '') => String(text).match(WORD_PATTERN) || [];

export const countWordSyllables = (word = '') => {
  const clean = String(word).toLocaleLowerCase().replace(/[^a-z]/g, '');
  if (!clean) return 0;
  const groups = clean.match(VOWEL_GROUP_PATTERN) || [];
  let count = groups.length;
  const keepsFinalE = /[^aeiouy]le$/.test(clean) || /(ee|ye)$/.test(clean);
  if (count > 1 && clean.endsWith('e') && !keepsFinalE) count -= 1;
  return Math.max(1, count);
};

export const countSyllables = (text = '') => (
  wordsIn(text).reduce((total, word) => total + countWordSyllables(word), 0)
);

export const pocketMeter = (text = '') => {
  const count = countSyllables(text);
  return { count, filled: Math.min(16, count), crowded: count > 16 };
};

export const lineEndingWord = (text = '') => {
  const words = wordsIn(text);
  return words.length ? words[words.length - 1].toLocaleLowerCase() : '';
};

export const rhymeEndingKey = (text = '') => {
  const word = lineEndingWord(text).replace(/[^a-z]/g, '');
  if (!word) return '';
  const match = word.match(/[aeiouy]+[^aeiouy]*$/);
  return match ? match[0] : word;
};

export const groupRhymeFamilies = (lines = []) => {
  const familyIndexes = new Map();
  return lines.map((line) => {
    const word = lineEndingWord(line?.text || line);
    const key = rhymeEndingKey(word);
    if (key && !familyIndexes.has(key)) familyIndexes.set(key, familyIndexes.size);
    return {
      word,
      key,
      family: key ? familyIndexes.get(key) : null,
    };
  });
};

export const hookNeedsRhymeTip = (sections = []) => {
  const hook = sections.find((section) => section.label === 'Hook');
  const keys = (hook?.lines || []).map((line) => rhymeEndingKey(line.text)).filter(Boolean);
  return keys.length >= 2 && new Set(keys).size > 1;
};

export const swapLastWord = (text = '', replacement = '') => {
  const source = String(text);
  const words = [...source.matchAll(WORD_PATTERN)];
  const cleanReplacement = lineEndingWord(replacement);
  if (!words.length || !cleanReplacement) return source;
  const last = words[words.length - 1];
  return `${source.slice(0, last.index)}${cleanReplacement}${source.slice(last.index + last[0].length)}`;
};

export const buildStyleString = (chips = [], bpm = 90, cleanRhymes = true) => {
  const cleanChips = chips.map((chip) => String(chip).trim()).filter(Boolean);
  const tempo = Math.max(50, Math.min(200, Number(bpm) || 90));
  return [...cleanChips, `around ${tempo} BPM`, ...(cleanRhymes ? ['clean lyrics'] : [])].join(', ');
};

export const missingSectionLabels = (sections = [], order = SECTION_ORDER) => {
  const present = new Set(sections.map((section) => section.label));
  return order.filter((label) => !present.has(label));
};

export const totalLineCount = (sections = []) => (
  sections.reduce((total, section) => total + (section.lines || []).length, 0)
);

const updateSection = (sheet, sectionIndex, updater) => ({
  ...sheet,
  sections: sheet.sections.map((section, index) => (
    index === sectionIndex ? updater(section) : section
  )),
});

export const sheetReducer = (sheet, action) => {
  switch (action.type) {
    case 'reset':
      return { ...action.sheet };
    case 'edit_title':
      return { ...sheet, title: action.title.slice(0, 80) };
    case 'edit_style':
      return { ...sheet, style: action.style.slice(0, 400) };
    case 'edit_line':
      return updateSection(sheet, action.section, (section) => ({
        ...section,
        lines: section.lines.map((line, index) => (
          index === action.line ? { ...line, text: action.text.slice(0, 140) } : line
        )),
      }));
    case 'add_line': {
      const section = sheet.sections[action.section];
      if (!section || section.lines.length >= MAX_LINES_PER_SECTION) return sheet;
      if (totalLineCount(sheet.sections) >= MAX_SONG_LINES) return sheet;
      return updateSection(sheet, action.section, (current) => ({
        ...current,
        lines: [...current.lines, { text: '', source_answer_ids: [] }],
      }));
    }
    case 'remove_line':
      return updateSection(sheet, action.section, (section) => ({
        ...section,
        lines: section.lines.filter((_line, index) => index !== action.line),
      }));
    case 'restore_line':
      if (sheet.sections[action.section]?.lines.length >= MAX_LINES_PER_SECTION) return sheet;
      if (totalLineCount(sheet.sections) >= MAX_SONG_LINES) return sheet;
      return updateSection(sheet, action.section, (section) => {
        const lines = [...section.lines];
        lines.splice(Math.min(action.line, lines.length), 0, action.value);
        return { ...section, lines };
      });
    case 'move_line': {
      const section = sheet.sections[action.section];
      const target = action.line + action.direction;
      if (!section || target < 0 || target >= section.lines.length) return sheet;
      return updateSection(sheet, action.section, (current) => {
        const lines = [...current.lines];
        [lines[action.line], lines[target]] = [lines[target], lines[action.line]];
        return { ...current, lines };
      });
    }
    case 'add_section': {
      const order = action.order || SECTION_ORDER;
      if (!missingSectionLabels(sheet.sections, order).includes(action.label)) return sheet;
      const sections = [...sheet.sections, { label: action.label, lines: [] }]
        .sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
      return { ...sheet, sections };
    }
    case 'edit_cadence': {
      const cadence = [...(sheet.cadence || [])];
      const index = cadence.findIndex((item) => item.label === action.label);
      const next = { label: action.label, note: action.note.slice(0, 160) };
      if (index === -1) cadence.push(next);
      else cadence[index] = next;
      return { ...sheet, cadence };
    }
    default:
      return sheet;
  }
};

export const editableSheetPayload = (sheet = {}) => ({
  title: sheet.title || '',
  style: sheet.style || '',
  cadence: (sheet.cadence || []).filter((item) => String(item.note || '').trim()),
  sections: (sheet.sections || []).map((section) => ({
    ...section,
    lines: (section.lines || []).filter((line) => String(line.text || '').trim()),
  })),
});

export const PIZZUL_TIPS = {
  editor_open: {
    pose: 'thinking',
    line: 'Mumble your flow first. Then fill the pocket with words.',
  },
  crowded: {
    pose: 'confused',
    line: "That bar's crowded. Cut a word or split it in two.",
  },
  hook_rhyme: {
    pose: 'thinking',
    line: 'Hooks hit harder when the last words rhyme. Tap the last word for options.',
  },
  first_lookup: {
    pose: 'happy',
    line: "'From you' are your own words. Use those first. Keeps it yours.",
  },
  missing_lock: {
    pose: 'confused',
    line: 'You locked that phrase for a reason. Want it back in?',
  },
  first_copy: {
    pose: 'satisfied',
    line: 'Read the flow notes before you hit record.',
  },
  just_write: {
    pose: 'writing',
    line: 'Blank page, no rules. Start with the hook.',
  },
};

export const claimTip = (seen = [], key, triggered = true, tips = PIZZUL_TIPS) => {
  if (!triggered || !tips[key] || seen.includes(key)) return { seen, next: null };
  return { seen: [...seen, key], next: key };
};
